import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FacultyQueueSubmission } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import {
  countRowReviewStatuses,
  rowReviewStatusMarker,
  submissionStatusLabel,
  submissionStatusVariant,
} from '../../lib/submissionWorkflowLabels.js';

function formatRowReviewBreakdown(submission: FacultyQueueSubmission): string {
  const counts = countRowReviewStatuses(submission.rows);
  return `${counts.autoAccept} auto · ${counts.studentReview} student · ${counts.instructorReview} instructor`;
}

export function ReviewQueuePage() {
  const [submissions, setSubmissions] = useState<FacultyQueueSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    setError(null);
    setForbidden(false);
    return api
      .listWorkflowSubmissions()
      .then((res) => setSubmissions(res.data as FacultyQueueSubmission[]))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
          setError('You do not have access to the faculty review queue.');
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load review queue');
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const allReviewComplete =
    submissions.length > 0 && submissions.every((s) => s.reviewNeededCount === 0);

  return (
    <div className="page">
      <PageHeader title="Review Queue" subtitle="Clinical sheets awaiting faculty approval" />

      {error && !loading && (
        <ErrorBanner message={error} onRetry={forbidden ? undefined : () => void load()} />
      )}

      {loading ? (
        <LoadingState message="Loading review queue…" />
      ) : !error && submissions.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<span aria-hidden>✓</span>}
            title="Review queue is empty"
            description="When students submit clinical sheets for review, they will appear here."
          />
        </div>
      ) : !error ? (
        <>
          {allReviewComplete && (
            <div className="banner banner--success">
              All queued submissions have resolved row review flags — open a sheet to complete faculty disposition.
            </div>
          )}
          <div className="card">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Row review breakdown</th>
                    <th>Needs Review</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => {
                    const counts = countRowReviewStatuses(submission.rows);
                    return (
                      <tr key={submission.id}>
                        <td>{submission.studentName}</td>
                        <td>
                          {submission.submittedAt
                            ? new Date(submission.submittedAt).toLocaleString()
                            : '—'}
                        </td>
                        <td>
                          <StatusBadge
                            status={submissionStatusLabel(submission.status)}
                            variant={submissionStatusVariant(submission.status)}
                          />
                        </td>
                        <td>{submission.rows.length}</td>
                        <td>
                          <span className="text-sm">{formatRowReviewBreakdown(submission)}</span>
                          {counts.instructorReview > 0 && (
                            <span
                              className="review-row__marker"
                              aria-label={`${counts.instructorReview} row(s) need instructor review`}
                            >
                              {rowReviewStatusMarker('INSTRUCTOR_REVIEW_REQUIRED')}{' '}
                              {counts.instructorReview}
                            </span>
                          )}
                        </td>
                        <td>{submission.reviewNeededCount}</td>
                        <td className="actions-cell">
                          <Link
                            to={`/instructor/review/${submission.id}`}
                            className="btn btn-primary btn-sm"
                          >
                            Open Review
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
